<script setup>
import { ref, provide, useSlots, onMounted } from 'vue'
import { useBreakpoints } from '@vueuse/core'
const breakpoints = useBreakpoints({
    lg: 1024
})
const aboveLg = breakpoints.greaterOrEqual('lg');

const props = defineProps({})

const timeslots = ref([])

function register(timeslot) {
    timeslots.value.push(timeslot)
}
function unregister(timeslot) {
    timeslots.value = timeslots.value.filter(({ uid }) => uid !== timeslot.uid)
}
provide('time-table', { register, unregister })

function toggle({ uid }) {
    if (aboveLg.value) { return }

    timeslots.value
        .forEach((timeslot) => {
            if (timeslot.uid === uid) {
                timeslot.expanded = !timeslot.expanded
            } else {
                timeslot.expanded = timeslot.uid === uid
            }
        })
}


function isActive(timeslot) {
    if (aboveLg.value) { return true }
    return timeslot.expanded
}
function hasDetails(timeslot) {
    return timeslot?.details && timeslot.details?.length > 0
}

const slots = useSlots()
const icon = ref(null)
onMounted(() => { icon.value = slots?.icon() })
</script>

<template>
    <div class="time-table">
        <template v-for="(timeslot, index) in timeslots" :key="index">

            <div :class="[
                'time-slot',
                `time-slot--${timeslot.type}`,
                {
                    'time-slot--interactive': hasDetails(timeslot),
                    'time-slot--active': isActive(timeslot),
                }
            ]">

                <div class="time-slot__header" @click="toggle(timeslot)">
                    <div class="time-slot__time">
                        <time :datetime="timeslot.startTime">{{ timeslot.startTime }}</time>
                        <span> - </span>
                        <time :datetime="timeslot.endTime">{{ timeslot.endTime }}</time>
                    </div>

                    <div
                        class="time-slot__summary"
                    >
                        <component
                            :is="vnode"
                            v-for="(vnode, index) in timeslot.summary"
                            :key="index"
                        />
                    </div>

                    <div class="time-slot__icon" v-if="hasDetails(timeslot)">
                        <component
                            :is="vnode"
                            v-for="(vnode, index) in icon"
                            :key="index"
                        />
                    </div>

                </div>

                <template v-if="hasDetails(timeslot)">
                    <expand-transition>
                        <div class="time-slot__details" v-if="isActive(timeslot)">
                            <div>
                                <component
                                    :is="vnode"
                                    v-for="(vnode, index) in timeslot.details"
                                    :key="index"
                                />
                            </div>
                        </div>
                    </expand-transition>
                </template>

            </div>
        </template>

        <slot />

    </div>
</template>

<style lang="scss">
@use "@/scss/framework" as *;

@layer components.time-table {
    .time-table {
        display: grid;
    }
}

@layer components.time-slot {
    :root {
        --time-slot-color--break: var(--color-radical-red);
        --time-slot-color--text: var(--color-turquoise-blue);
        --time-slot-color--speaker: var(--color-white);
        --time-slot-separator-color: color-mix(in srgb, var(--color-white) 30%, transparent);
        --time-slot-header-v-padding: #{clamped(26px, 40px)};
        --time-slot-column-gap: #{clamped(40px, 40px)};

        --time-slot-headline-font: var(--font-h3);
        @include v((
            --time-slot-time-font: (
                default: 800 22px / 86% var(--font-family-headline),
                sm: 800 #{clamped(20px, 26px)} / 86% var(--font-family-headline)
            )
        ));

        --time-slot-icon-color: var(--color-radical-red);
        --time-slot-icon-color--hover: var(--color-radical-red--hover);
        --time-slot-icon-color--active: var(--color-turquoise-blue);
        --time-slot-icon-color--active--hover: var(--color-turquoise-blue--hover);
        --accordion-separator-color: color-mix(in srgb, var(--color-white) 30%, transparent);
        --time-slot-headline-v-padding: #{clamped(26px, 40px)};

    }

    .time-slot {
        display: grid;
        border-bottom: 1px solid var(--time-slot-separator-color);

        grid-template-columns:
            [header-start summary-start details-start] 0px
            [time-start] auto [time-end]
            var(--time-slot-column-gap, 0px)
            1fr
            var(--time-slot-column-gap, 0px)
            [icon-start] auto [icon-end]
            0px [header-end summary-end details-end]
        ;

        grid-template-rows:
            [header-start time-start icon-start] auto [time-end icon-end]
            20px
            [summary-start] auto [summary-end header-end]
            0px
            [details-start] auto [details-end]
        ;

        @include mq-from(sm) {
            grid-template-columns:
                [header-start details-start] 0px
                [time-start] auto [time-end]
                var(--time-slot-column-gap, 0px)
                [summary-start] 1fr [summary-end]
                var(--time-slot-column-gap, 0px)
                [icon-start] auto [icon-end]
                0px [header-end details-end]
            ;
            grid-template-rows:
                [header-start time-start summary-start icon-start] auto [header-end time-end summary-end icon-end]
                0px
                [details-start] auto [details-end]
            ;
        }

        @include mq-from(lg) {
            grid-template-columns:
                [header-start] 0px
                [time-start] auto [time-end]
                var(--time-slot-column-gap, 0px)
                [summary-start] 1fr [summary-end header-end]
                var(--time-slot-column-gap, 0px)
                [details-start] 2fr [details-end]
                0px
            ;
            grid-template-rows:
                [header-start time-start summary-start details-start]
                auto
                [header-end time-end summary-end details-end]
            ;
        }

        &__header {
            grid-area: header;
            display: grid;
            grid-template-columns: subgrid;
            grid-template-rows: subgrid;
            padding: var(--time-slot-header-v-padding) 0;

            @include hover {
                .time-slot__icon {
                    svg {
                        color: var(--time-slot-icon-color--hover);
                    }
                }
            }
        }

        &__time {
            grid-area: time;
            font: var(--time-slot-time-font);
        }

        &__summary {
            grid-area: summary;


            :is(h1,h2,h3,h4,h5,h6) {
                font: var(--time-slot-headline-font);
                margin: 0 0 10px;
            }
            :is(p, span, div) {
                margin: 0;
            }

            & > :first-child { margin-top: 0; }
            & > :last-child { margin-bottom: 0; }
        }

        &__icon {
            grid-area: icon;

            font: var(--time-slot-headline-font);
            width: 20px;
            height: 1lh;

            svg {
                @include svg-auto-color;
                width: 100%;
                height: 100%;
                color: var(--time-slot-icon-color);
                transform: rotate(0turn);
                transition:
                    transform var(--duration-default) var(--easing-default),
                    color var(--duration-default) var(--easing-default);
            }

            @include mq-from(lg) {
                display: none;
            }
        }

        &__details {
            grid-area: details;

            :is(h1,h2,h3,h4,h5,h6) {
                font: var(--time-slot-headline-font);
                margin: 0 0 10px;
            }

            :is(p, span, div) {
                margin: 0;
            }
            > div {
                & > :first-child { margin-top: 0;}
                & > :last-child { margin-bottom: 0; }

                padding: 0 0 20px;
                @include mq-from(lg) {
                    padding: var(--time-slot-header-v-padding) 0;
                }
            }

        }


        &--interactive {

            .time-slot__header {

                cursor: pointer;
                @include mq-from(lg) { cursor: default; }
            }
        }

        &--active {
            .time-slot__icon {

                svg {
                    color: var(--time-slot-icon-color--active);
                    transform: rotate(-0.5turn);
                }

            }
            .time-slot__header {

                @include hover {
                    svg {
                        color: var(--time-slot-icon-color--active--hover);
                    }
                };
            }
        }

        &--speaker {}
        &--break {
            .time-slot__time,
            .time-slot__summary :is(h1,h2,h3,h4,h5,h6) {
                color: var(--time-slot-color--break);
            }

            @include mq-from(lg) {
                .time-slot__header {
                    grid-column: header-start / details-end;
                }
                .time-slot__summary {
                    grid-column: summary-start / details-end;
                }
            }
        }

        &--text {
            --time-slot-icon-color: var(--color-turquoise-blue);
            --time-slot-icon-color--hover: var(--color-turquoise-blue--hover);
            --time-slot-icon-color--active: var(--color-turquoise-blue);
            --time-slot-icon-color--active--hover: var(--color-turquoise-blue--hover);

            .time-slot__time,
            .time-slot__summary :is(h1,h2,h3,h4,h5,h6),
            .time-slot__details :is(h1,h2,h3,h4,h5,h6) {

                color: var(--time-slot-color--text);
            }
        }
    }
}
</style>
